import React, { useEffect, useState } from "react";
import {
  IssueAPI,
  TaskUsersAPI,
  ProjectAPI,
} from "../api/apiClient";

import { useAuth } from "../context/AuthContext";
import { toast } from "react-hot-toast";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { TaskUser } from "../types/taskuser";
import { ProjectUser } from "../types/ProjectUser";

export default function IssueDetails({
  projectId,
  taskId,
}: {
  projectId: number;
  taskId: number;
}) {
  const [loading, setLoading] = useState(true);

  const [issue, setIssue] = useState<any>(null);

  // Task groups
  const [taskGroups, setTaskGroups] = useState<
    { taskGroupNo: number; taskGroupName: string }[]
  >([]);

  // Project company
  const [projectCompanyNo, setProjectCompanyNo] = useState<number | null>(
    null
  );

  // Participants
  const [participants, setParticipants] = useState<TaskUser[]>([]);

  // Users assigned/available in project
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);

  const [newUser, setNewUser] = useState<number | "">("");

  const [role, setRole] = useState<string>("");

  const { user } = useAuth();

  // ============================================================
  // LOAD ISSUE
  // ============================================================

  const load = () => {
    setLoading(true);

    IssueAPI.getDetails(projectId, taskId)
      .then(setIssue)
      .catch(() => toast.error("Failed to load issue details"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!projectId || !taskId) return;

    load();
  }, [projectId, taskId]);

  // ============================================================
  // LOAD PROJECT COMPANY
  //
  // Project already contains CompanyNo.
  // We use the Project to determine which company this issue
  // belongs to.
  // ============================================================

  useEffect(() => {
    if (!projectId) return;

    const loadProjectCompany = async () => {
      try {
        const projects = await ProjectAPI.getAll();

        const project = projects.find(
          (p: any) => Number(p.projectNo) === Number(projectId)
        );

        if (project) {
          setProjectCompanyNo(Number(project.companyNo));
        } else {
          setProjectCompanyNo(null);
          console.warn(
            "Project not found while determining company:",
            projectId
          );
        }
      } catch (error) {
        console.error("Failed to load project company:", error);
        setProjectCompanyNo(null);
      }
    };

    loadProjectCompany();
  }, [projectId]);

  // ============================================================
  // LOAD TASK GROUPS
  // ============================================================

  useEffect(() => {
    if (!projectId) return;

    ProjectAPI.getTaskGroups(projectId)
      .then(setTaskGroups)
      .catch(() => toast.error("Failed to load task groups"));
  }, [projectId]);

  // ============================================================
  // LOAD PARTICIPANTS AND PROJECT USERS
  // ============================================================

  useEffect(() => {
    if (!taskId || !projectId) return;

    TaskUsersAPI.list(taskId)
      .then(setParticipants)
      .catch(console.error);

    ProjectAPI.getUsers(projectId)
      .then(setProjectUsers)
      .catch(console.error);
  }, [taskId, projectId]);

  // ============================================================
  // COMPANY-BASED ADMIN CHECK
  //
  // IMPORTANT:
  // We no longer use:
  //
  // user.userType
  //
  // because UserType is now stored in UserCompany.
  //
  // We first find the company of the current Project,
  // then check the user's role inside that company.
  //
  // Admin + SuperAdmin have full task editing access.
  // ============================================================

  const isAdmin =
    !!user &&
    !!projectCompanyNo &&
    user.companies?.some(
      (company: any) =>
        Number(company.companyNo) === Number(projectCompanyNo) &&
        ["admin", "superadmin"].includes(
          (company.userType ?? "").toLowerCase()
        )
    ) === true;

  // ============================================================
  // EDIT PERMISSION
  //
  // User can edit when:
  //
  // 1. They are Task Owner
  // 2. They are Task Assignee
  // 3. They are Admin/SuperAdmin of the Project's Company
  // ============================================================

  const canEdit =
    !!issue &&
    (issue.taskOwner === user?.userNo ||
      issue.taskAssignee === user?.userNo ||
      isAdmin);

  // ============================================================
  // UPDATE ISSUE
  // ============================================================

  const handleUpdate = async (field: string, value: any) => {
    try {
      await IssueAPI.update(projectId, taskId, {
        [field]: value,
      });

      toast.success("Updated!");

      load();
    } catch (err: any) {
      toast.error(err.message || "Update failed");
    }
  };

  // ============================================================
  // ADD PARTICIPANT
  // ============================================================

  const addUser = async () => {
    if (!newUser) return;

    try {
      await TaskUsersAPI.assign(taskId, newUser, role);

      setNewUser("");
      setRole("");

      const updatedParticipants = await TaskUsersAPI.list(taskId);

      setParticipants(updatedParticipants);

      toast.success("Participant added");
    } catch (err: any) {
      toast.error(err.message || "Failed to add participant");
    }
  };

  // ============================================================
  // REMOVE PARTICIPANT
  // ============================================================

  const removeUser = async (userNo: number) => {
    try {
      await TaskUsersAPI.unassign(taskId, userNo);

      const updatedParticipants = await TaskUsersAPI.list(taskId);

      setParticipants(updatedParticipants);

      toast.success("Participant removed");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove participant");
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  // ============================================================
  // ISSUE NOT FOUND
  // ============================================================

  if (!issue) {
    return <div className="p-4">Issue not found</div>;
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="w-full h-full overflow-y-auto space-y-5 px-4 py-4 md:max-w-2xl">

      {/* ========================================================
          TITLE
      ======================================================== */}

      <textarea
        defaultValue={issue.title}
        placeholder="Enter task name..."
        onBlur={(e) =>
          handleUpdate("title", e.target.value)
        }
        className="w-full text-2xl font-semibold border-b border-gray-300 focus:border-blue-500 focus:outline-none resize-none"
        rows={2}
      />

      {/* ========================================================
          DESCRIPTION
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Description
        </label>

        <textarea
          defaultValue={issue.description}
          onBlur={(e) =>
            handleUpdate("description", e.target.value)
          }
          className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          rows={4}
          disabled={!canEdit}
        />
      </div>

      {/* ========================================================
          ASSIGNEE
          
          Company Admin/SuperAdmin can change assignee.
          Task Owner can also change assignee.
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Assignee
        </label>

        <select
          value={issue.taskAssignee ?? ""}
          onChange={(e) =>
            handleUpdate(
              "taskAssignee",
              e.target.value
                ? Number(e.target.value)
                : null
            )
          }
          className="border rounded-md p-2 w-full"
          disabled={
            !isAdmin &&
            issue.taskOwner !== user?.userNo
          }
        >
          <option value="">
            — Select Assignee —
          </option>

           {projectUsers.map((u) => (
            <option
              key={u.userNo}
              value={u.userNo}
            >
              {u.userName}
            </option>
          ))} 
         
        </select>
      </div>

      {/* ========================================================
          STATUS
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Status
        </label>

        <select
          value={issue.status ?? ""}
          onChange={(e) =>
            handleUpdate(
              "status",
              e.target.value
            )
          }
          className="border rounded-md p-2 w-full"
          disabled={!canEdit}
        >
          <option value="Open">
            Open
          </option>

          <option value="Closed">
            Closed
          </option>
        </select>
      </div>

      {/* ========================================================
          TASK GROUP
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Task Group
        </label>

        <select
          value={issue.taskGroupNo ?? ""}
          onChange={(e) =>
            handleUpdate(
              "taskGroupNo",
              e.target.value
                ? Number(e.target.value)
                : null
            )
          }
          className="border rounded-md p-2 w-full"
          disabled={!canEdit}
        >
          <option value="">
            — Select Task Group —
          </option>

          {taskGroups.map((g) => (
            <option
              key={g.taskGroupNo}
              value={g.taskGroupNo}
            >
              {g.taskGroupName}
            </option>
          ))}
        </select>
      </div>

      {/* ========================================================
          PROGRESS
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Progress — {issue.progressPercent ?? 0}%
        </label>

        <input
          type="range"
          min="0"
          max="100"
          defaultValue={
            issue.progressPercent ?? 0
          }
          onMouseUp={(e) =>
            handleUpdate(
              "progress",
              Number(
                (e.target as HTMLInputElement)
                  .value
              )
            )
          }
          onTouchEnd={(e) =>
            handleUpdate(
              "progress",
              Number(
                (e.target as HTMLInputElement)
                  .value
              )
            )
          }
          className="w-full"
          disabled={!canEdit}
        />
      </div>

      {/* ========================================================
          NEXT REMINDER DATE
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Next Reminder Date
        </label>

        <DatePicker
          selected={
            issue.nextReminderDate
              ? new Date(
                  issue.nextReminderDate
                )
              : null
          }
          onChange={(d) => {
            if (!d) return;

            const yyyy =
              d.getFullYear();

            const mm = String(
              d.getMonth() + 1
            ).padStart(2, "0");

            const dd = String(
              d.getDate()
            ).padStart(2, "0");

            handleUpdate(
              "nextReminderDate",
              `${yyyy}-${mm}-${dd}`
            );
          }}
          dateFormat="dd-MMM-yyyy"
          className="border px-2 py-1 rounded-md w-full"
          disabled={!canEdit}
        />
      </div>

      {/* ========================================================
          OVERALL DUE DATE
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Overall Due Date
        </label>

        <DatePicker
          selected={
            issue.overallDueDate
              ? new Date(
                  issue.overallDueDate
                )
              : null
          }
          onChange={(d) => {
            if (!d) return;

            const yyyy =
              d.getFullYear();

            const mm = String(
              d.getMonth() + 1
            ).padStart(2, "0");

            const dd = String(
              d.getDate()
            ).padStart(2, "0");

            handleUpdate(
              "overallDueDate",
              `${yyyy}-${mm}-${dd}`
            );
          }}
          dateFormat="dd-MMM-yyyy"
          className="border px-2 py-1 rounded-md w-full"
          disabled={!canEdit}
        />
      </div>

      {/* ========================================================
          NEXT ACTION
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Next Action
        </label>

        <input
          defaultValue={
            issue.nextAction || ""
          }
          placeholder="Type next action and press Enter"
          className="w-full border px-2 py-2 rounded-md"
          disabled={!canEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const value =
                (
                  e.target as HTMLInputElement
                ).value;

              if (!value.trim()) return;

              handleUpdate(
                "nextAction",
                value
              );

              (
                e.target as HTMLInputElement
              ).value = "";
            }
          }}
        />
      </div>

      {/* ========================================================
          SUBCONTRACTOR
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Subcontractor
        </label>

        <select
          value={issue.Subcontractor ?? ""}
          onChange={(e) =>
            handleUpdate(
              "Subcontractor",
              e.target.value
                ? Number(e.target.value)
                : null
            )
          }
          className="border rounded-md p-2 w-full"
          disabled={!canEdit}
        >
          <option value="">
            — Select —
          </option>

          {participants.map((p) => (
            <option
              key={p.userNo}
              value={p.userNo}
            >
              {p.userName}
            </option>
          ))}
        </select>
      </div>

      {/* ========================================================
          CLIENT
      ======================================================== */}

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-600">
          Client
        </label>

        <select
          value={issue.Client ?? ""}
          onChange={(e) =>
            handleUpdate(
              "Client",
              e.target.value
                ? Number(e.target.value)
                : null
            )
          }
          className="border rounded-md p-2 w-full"
          disabled={!canEdit}
        >
          <option value="">
            — Select —
          </option>

          {participants.map((p) => (
            <option
              key={p.userNo}
              value={p.userNo}
            >
              {p.userName}
            </option>
          ))}
        </select>
      </div>

      {/* ========================================================
          PARTICIPANTS
      ======================================================== */}

      <div className="border rounded p-4 bg-white">
        <h3 className="font-semibold mb-2">
          Participants
        </h3>

        {/* Existing participants */}

        {participants.length === 0 ? (
          <div className="text-sm text-gray-500">
            No participants yet
          </div>
        ) : (
          <ul className="space-y-2">
            {participants.map((p) => (
              <li
                key={p.userNo}
                className="flex items-center justify-between bg-gray-100 px-3 py-1 rounded"
              >
                <span>
                  {p.userName}
                  {p.role
                    ? ` (${p.role})`
                    : ""}
                </span>

                <button
                  className="text-red-500 text-sm hover:underline"
                  disabled={!canEdit}
                  onClick={() =>
                    removeUser(p.userNo)
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* ======================================================
            ADD PARTICIPANT
        ====================================================== */}

        <div className="flex flex-col gap-2 mt-3 md:flex-row">

          <select
            value={newUser}
            disabled={!canEdit}
            onChange={(e) =>
              setNewUser(
                e.target.value
                  ? Number(e.target.value)
                  : ""
              )
            }
            className="border rounded p-2 w-full md:flex-1"
          >
            <option value="">
              — Add participant —
            </option>

            {projectUsers
              .filter(
                (u) =>
                  !participants.some(
                    (p) =>
                      p.userNo ===
                      u.userNo
                  )
              )
              .map((u) => (
                <option
                  key={u.userNo}
                  value={u.userNo}
                >
                  {u.userName}
                </option>
              ))}
          </select>

          <input
            placeholder="Role (optional)"
            className="border rounded px-2 py-1 w-full md:w-32"
            value={role}
            disabled={!canEdit}
            onChange={(e) =>
              setRole(e.target.value)
            }
          />

          <button
            disabled={
              !newUser || !canEdit
            }
            onClick={addUser}
            className="px-4 py-2 bg-blue-600 text-white rounded w-full md:w-auto disabled:bg-gray-400"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}